// VertexAIService: Handles inpainting requests to Google Vertex AI Imagen 3 Capability API via backend proxy
// Note: Imagen 3 capability model is used for editing/inpainting (Imagen 4 is for generation only)
import logger from '../utils/logger';
import { supabase } from '../lib/supabase';

export interface InpaintRequest {
  imageUrl: string;
  maskUrl: string;
  prompt: string;
  model?: string;
}

export interface InpaintResponse {
  imageUrl: string;
  processingTime?: number;
}

export interface InpaintError {
  message: string;
  code?: string;
  details?: any;
}

class VertexAIService {
  // Use relative URL in production, localhost for development
  private baseUrl: string = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');

  async inpaint(request: InpaintRequest): Promise<InpaintResponse> {
    try {
      logger.debug('[VertexAI] Sending inpaint request:', {
        prompt: request.prompt.substring(0, 100),
        imageUrlLength: request.imageUrl.length,
        maskUrlLength: request.maskUrl.length
      });

      // Get current user for credit tracking
      const { data: { user } } = await supabase.auth.getUser();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id;
      }

      const response = await fetch(`${this.baseUrl}/api/vertexai/inpaint`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          imageUrl: request.imageUrl,
          maskUrl: request.maskUrl,
          prompt: request.prompt
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        
        logger.error('[VertexAI] API request failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });

        throw {
          message: errorData.error || `API request failed with status ${response.status}`,
          code: response.status.toString(),
          details: errorData.details
        } as InpaintError;
      }

      const data = await response.json();
      
      logger.debug('[VertexAI] Inpainting successful:', {
        imageUrl: data.imageUrl?.substring(0, 50),
        processingTime: data.processingTime
      });

      return {
        imageUrl: data.imageUrl,
        processingTime: data.processingTime,
      };
    } catch (error: any) {
      // Handle network errors
      if (error.message?.includes('fetch')) {
        logger.error('[VertexAI] Network error:', error);
        throw {
          message: 'Unable to connect to backend server. Please ensure the server is running.',
          code: 'NETWORK_ERROR',
          details: error
        } as InpaintError;
      }

      // Re-throw InpaintError
      if (error.message && error.code) {
        throw error;
      }

      // Handle unexpected errors
      logger.error('[VertexAI] Unexpected error:', error);
      throw {
        message: error.message || 'An unexpected error occurred during inpainting',
        code: 'UNKNOWN_ERROR',
        details: error
      } as InpaintError;
    }
  }

  isConfigured(): boolean {
    // Always return true since API key is checked on server
    return true;
  }
}

export default new VertexAIService();
